import "@mdi/font/css/materialdesignicons.css";
import { createVueI18nAdapter } from "vuetify/locale/adapters/vue-i18n";
import { useI18n } from "vue-i18n";
import "vuetify/styles";
import { createVuetify as createVuetifyFromLib } from "vuetify";

/**
 * https://vuetifyjs.com/en/introduction/why-vuetify/#feature-guides
 * @param {import("vue-i18n").I18n} i18n
 * @returns {import("vue").Plugin}
 */
export function createVuetifyInstance(i18n) {
  return createVuetifyFromLib({
    lang: {
      t: (key, ...params) => i18n.t(key, params),
    },
    theme: {
      defaultTheme: "light",
      themes: {
        light: {
          dark: false,
          colors: {
            primary: "#4A5DB8",
            "link-primary": "#4A5DB8",
            "text-primary": "#4A5DB8",
            "on-primary": "#FFFFFF",
            secondary: "#2AB39A",
            "on-secondary": "#FFFFFF",
            "link-secondary": "#2AB39A",
            "text-secondary": "#2AB39A",
            accent: "#2E3C82",
            // Plan / premium markers (crown, "from Regular" chips): warm, distinct from the blue UI.
            premium: "#D96C2B",
            "on-premium": "#FFFFFF",
            "link-accent": "#2E3C82",
            "text-accent": "#2E3C82",
            "on-accent": "#FFFFFF",
            error: "#d93125",
            warning: "#e36c00",
            info: "#008ac8",
            success: "#019002",
            background: "#F3F3F3",
            header: "#FBFBFD",
            footer: "#A6ADB4",
            "on-footer": "#FFFFFF",
            third: "#ebeded",
          },
        },
      },
    },
    locale: {
      adapter: createVueI18nAdapter({ i18n, useI18n }),
    },
    defaults: {
      VAppBar: {
        density: "comfortable",
      },
      VTextField: {
        density: "comfortable",
        variant: "outlined",
      },
      VNumberInput: {
        density: "comfortable",
        variant: "outlined",
      },
      VSelect: {
        density: "comfortable",
        variant: "outlined",
        persistentHint: true,
      },
      VSwitch: {
        color: "primary",
        density: "comfortable",
        variant: "outlined",
        hideDetails: "auto",
        persistentHint: true,
      },
      VRadio: {
        density: "comfortable",
        variant: "outlined",
        hideDetails: "auto",
        persistentHint: true,
      },
      VCheckbox: {
        density: "comfortable",
        variant: "outlined",
        hideDetails: "auto",
        persistentHint: true,
      },
      VTextarea: {
        density: "comfortable",
        variant: "outlined",
      },
      VFileInput: {
        density: "comfortable",
        variant: "outlined",
      },
      VAutocomplete: {
        density: "comfortable",
        variant: "outlined",
        persistentHint: true,
      },
      VCombobox: {
        density: "comfortable",
        variant: "outlined",
        persistentHint: true,
      },
      VChip: {
        density: "comfortable",
        variant: "flat",
      },
      VBtn: {
        color: "secondary",
        variant: "elevated",
        rounded: "xl",
        VIcon: {
          size: "large",
        },
      },
      VCardActions: {
        VBtn: {
          color: "secondary",
          variant: "elevated",
          rounded: "xl",
          VIcon: {
            size: "large",
          },
        },
      },
      VCard: {
        rounded: "lg",
      },
      VList: {
        density: "comfortable",
        lines: "one",
      },
      VListItem: {
        density: "comfortable",
      },
      VAlert: {
        density: "comfortable",
      },
      VDialog: {
        width: "98%",
        maxWidth: "600",
      },
      VTooltip: {
        color: "white",
      },
      VToolbar: {
        density: "comfortable",
      },
      VDatePicker: {
        showAdjacentMonths: true,
      },
      VDataTable: {
        itemsPerPageOptions: [10, 25, 50, 100],
        mobile: null,
        mobileBreakpoint: "md",
      },
      VDataTableServer: {
        itemsPerPageOptions: [10, 25, 50, 100],
        mobile: null,
        mobileBreakpoint: "md",
      },
      VDataTableVirtual: {
        mobile: null,
        mobileBreakpoint: "md",
      },
      VTable: {
        mobile: null,
        mobileBreakpoint: "md",
        density: "comfortable",
      },
      VBtnToggle: {
        density: "comfortable",
        rounded: "0",
        tile: true,
        class: "flex-wrap",
      },
      VBtnGroup: {
        density: "comfortable",
        rounded: "0",
        tile: true,
        class: "flex-wrap",
        VBtn: {
          variant: "outlined",
          rounded: "0",
          class: "text-none mb-1",
          selectedClass: "btn-underlined v-btn--variant-tonal",
        },
      },
    },
  });
}
